import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, TextField, Typography } from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useField, useFormikContext } from 'formik';
import { showToast } from '@klpkonsern/ui';

import type { FormField } from '../interfaces';
import type { ProjectBudgetInfo } from '../services/rtkApi';
import {
    useGetProsjektByggListQuery,
    useLazyGetProjectBudgetDtosQuery,
    useLazyGetProjectBudgetsInfoQuery,
} from '../services/rtkApi';
import { getLocalizedLabel, handleBuildingOptionByValue } from '../utils';
import { Block } from '@klp/design';
import classNames from 'classnames';
import { getFormFieldStyles } from '../formFieldStyles';

const splitCommaSeparatedString = (value?: string) => {
    if (!value) return undefined;

    const trimmedValues = value.split(',').map((val) => val.trim());
    return trimmedValues.filter((val) => val !== '');
};

export const BudsjettinfoMultipleSelector = ({
    field,
    isReadOnly,
    isRequired,
}: {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
}) => {
    const { t } = useTranslation();
    const classes = getFormFieldStyles();
    const [formikField, meta] = useField<string>(field.id);
    const { setFieldValue, values } = useFormikContext();
    const [budgetnumberValue, setBudgetNumberValue] = useState<
        Array<string> | undefined
    >(splitCommaSeparatedString(formikField.value));
    const [fetchedFirmaId, setFetchedFirmaId] = useState('');
    const [
        getProjectBudgets,
        { data: projectBudgetDtos, isLoading, isFetching },
    ] = useLazyGetProjectBudgetDtosQuery();
    const [getProjectBudgetsInfo, { data: projectBudgetsInfo }] =
        useLazyGetProjectBudgetsInfoQuery();
    const { data: byggData } = useGetProsjektByggListQuery();

    const hasFetchedSelectedBudgets = useMemo(() => {
        if (!budgetnumberValue) return true;

        return (
            budgetnumberValue.length === projectBudgetsInfo?.length &&
            budgetnumberValue.every((budsjett) =>
                projectBudgetsInfo?.some((b) => b.budsjettnummer === budsjett),
            )
        );
    }, [budgetnumberValue, projectBudgetsInfo]);

    const budgetsExMvaSum = useMemo(() => {
        return (
            projectBudgetsInfo?.reduce(
                (partialsum, budget) => partialsum + budget.budgetExMva,
                0,
            ) ?? 0
        );
    }, [projectBudgetsInfo]);

    const budgetsMvaSum = useMemo(() => {
        return (
            projectBudgetsInfo?.reduce(
                (partialsum, budget) => partialsum + budget.mva,
                0,
            ) ?? 0
        );
    }, [projectBudgetsInfo]);

    const budgetsTotalSum = useMemo(() => {
        return (
            projectBudgetsInfo?.reduce(
                (partialsum, budget) => partialsum + budget.budgetTotal,
                0,
            ) ?? 0
        );
    }, [projectBudgetsInfo]);

    useEffect(() => {
        if (isReadOnly || !values?.['eiendomsnavn']) return;

        const bygg = handleBuildingOptionByValue(
            values['eiendomsnavn'],
            byggData,
        );

        if (
            bygg?.firmaId &&
            bygg?.firmaId !== '' &&
            bygg?.firmaId !== fetchedFirmaId
        ) {
            const fromDate = new Date(new Date().getFullYear(), 0, 1);
            getProjectBudgets({
                firmaId: bygg.firmaId,
                fromDate: fromDate.toISOString().split('T')[0],
            });
            setFetchedFirmaId(bygg.firmaId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, byggData]);

    useEffect(() => {
        if (!budgetnumberValue || budgetnumberValue.length === 0) return;

        if (hasFetchedSelectedBudgets) return;

        getProjectBudgetsInfo({
            budsjettnummer: budgetnumberValue.join(','),
        })
            .unwrap()
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budgetnumberValue, projectBudgetDtos]);

    useEffect(() => {
        if (isReadOnly || !projectBudgetsInfo) return;

        const aarsbudsjett = projectBudgetsInfo.reduce(
            (partialsum, budget) => partialsum + budget.budgetExMva,
            0,
        );
        setFieldValue('aarsbudsjett', aarsbudsjett.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectBudgetsInfo]);

    const handleChangeValue = (value?: Array<string>) => {
        if (!value || value.length === 0) {
            setBudgetNumberValue(undefined);
        } else {
            setBudgetNumberValue(value);
        }
        setFieldValue(field.id, value?.join(', '));
    };

    const initialValue =
        projectBudgetDtos?.filter((budget) =>
            budgetnumberValue?.includes(budget.budsjettnummer),
        ) ||
        budgetnumberValue?.map(
            (budsjett) => ({ budsjettnummer: budsjett }) as ProjectBudgetInfo,
        ) ||
        [];

    return (
        <>
            <Autocomplete
                id="budsjettnummer"
                multiple={true}
                selectOnFocus={true}
                clearOnBlur={true}
                handleHomeEndKeys={true}
                disabled={isReadOnly}
                options={projectBudgetDtos || []}
                getOptionSelected={(option, value) =>
                    option.budsjettnummer === value?.budsjettnummer
                }
                getOptionLabel={(option) =>
                    option.beskrivelse
                        ? `${option.budsjettnummer} - ${option.beskrivelse}`
                        : option.budsjettnummer
                }
                value={initialValue}
                onChange={(event, value) => {
                    const budsjettnummerList = value.map(
                        (budsjett) => budsjett.budsjettnummer,
                    );
                    handleChangeValue(budsjettnummerList);
                }}
                loading={isLoading || isFetching}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth={true}
                        className={classNames({
                            [classes.editableField]: !isReadOnly,
                            [classes.readOnlyField]: isReadOnly,
                        })}
                        required={isRequired}
                        variant="outlined"
                        disabled={!!isReadOnly}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading || isFetching ? (
                                        <CircularProgress color="inherit" />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        label={getLocalizedLabel(
                            `project.formFields.${field?.id}`,
                            field?.label,
                            field?.id,
                        )}
                        helperText={meta.touched ? meta.error : ''}
                        error={meta.touched && Boolean(meta.error)}
                    />
                )}
            />
            {projectBudgetsInfo && budgetnumberValue && (
                <Block paddingTop="tiny">
                    <Alert severity="info">
                        <Typography style={{ fontWeight: 'bold' }}>
                            {t('prosjekt.budsjettnummer.title', {
                                number: budgetnumberValue.join("', '"),
                            })}
                        </Typography>
                        <Typography variant="body1">
                            {t('prosjekt.budsjettnummer.budget', {
                                exMva: budgetsExMvaSum.toLocaleString('nb-NO'),
                                mva: budgetsMvaSum.toLocaleString('nb-NO'),
                                total: budgetsTotalSum.toLocaleString('nb-NO'),
                            })}
                        </Typography>
                    </Alert>
                </Block>
            )}
        </>
    );
};
